// apps/vendor/src/features/orders/detail/AssizeModal.tsx
//
// Amendment #7 — Assize modal: vendor enters PACKAGING dimensions
// (Length/Width/Height cm + Weight g) for an order item and persists them via
// PUT /vendor/orders/:orderId/items/:itemId/packaging-dimensions (useAssizeItem).
//
// Distinct from DimInputModal (Wave-6 Start-Production placeholder, no persist).
// This one actually writes — reachable from the orders list row + detail ActionBar.

import { useEffect, useState } from 'react';

import { Button, Input } from '@printway/ui';

import { useTranslation } from '../../../lib/i18n/index.js';
import { useAssizeItem, type PackagingDims } from '../api.js';

interface Props {
  open: boolean;
  orderId: string;
  itemId: string;
  /** Pre-fill from the item's existing packagingDimensions, if any. */
  initial?: PackagingDims | null;
  onClose: () => void;
}

export function AssizeModal({
  open,
  orderId,
  itemId,
  initial,
  onClose,
}: Props): JSX.Element | null {
  const { t } = useTranslation('orders');
  const assize = useAssizeItem();
  const [length, setLength] = useState('');
  const [width, setWidth] = useState('');
  const [height, setHeight] = useState('');
  const [weight, setWeight] = useState('');
  const [errorMsg, setErrorMsg] = useState<string | null>(null);

  // Pre-fill when (re)opened with an existing value.
  useEffect(() => {
    if (open) {
      setLength(initial?.l != null ? String(initial.l) : '');
      setWidth(initial?.w != null ? String(initial.w) : '');
      setHeight(initial?.h != null ? String(initial.h) : '');
      setWeight(initial?.weightGrams != null ? String(initial.weightGrams) : '');
      setErrorMsg(null);
    }
  }, [open, initial]);

  if (!open) return null;

  async function handleSave(): Promise<void> {
    setErrorMsg(null);
    const l = Number.parseFloat(length);
    const w = Number.parseFloat(width);
    const h = Number.parseFloat(height);
    const wg = Number.parseInt(weight, 10);
    if (
      !Number.isFinite(l) || l <= 0 ||
      !Number.isFinite(w) || w <= 0 ||
      !Number.isFinite(h) || h <= 0 ||
      !Number.isInteger(wg) || wg <= 0
    ) {
      setErrorMsg(t('assize.invalid'));
      return;
    }
    try {
      await assize.mutateAsync({ orderId, itemId, dims: { l, w, h, weightGrams: wg } });
      onClose();
    } catch (err) {
      setErrorMsg(
        err instanceof Error ? err.message : t('assize.saveFailed'),
      );
    }
  }

  return (
    <div
      role="dialog"
      aria-modal="true"
      data-testid="assize-modal"
      /* dark-ok: modal scrim, translucent black reads correctly in both modes */
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
    >
      <div className="w-full max-w-md rounded-xl border border-pw-border bg-pw-bg p-6 shadow-xl">
        <h2 className="text-lg font-semibold text-pw-text">{t('assize.modalTitle')}</h2>
        <p className="mt-2 text-sm text-pw-text-muted">{t('assize.modalDescription')}</p>

        <div className="mt-3 grid grid-cols-1 gap-3 sm:grid-cols-2">
          <Field label={t('assize.length')} value={length} onChange={setLength} testId="assize-length" />
          <Field label={t('assize.width')} value={width} onChange={setWidth} testId="assize-width" />
          <Field label={t('assize.height')} value={height} onChange={setHeight} testId="assize-height" />
          <Field label={t('assize.weight')} value={weight} onChange={setWeight} testId="assize-weight" />
        </div>

        {errorMsg ? (
          <p className="mt-3 rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
            {errorMsg}
          </p>
        ) : null}

        <div className="mt-4 flex justify-end gap-2">
          <Button
            type="button"
            variant="outline"
            data-testid="assize-cancel"
            onClick={onClose}
          >
            {t('assize.cancel')}
          </Button>
          <Button
            type="button"
            data-testid="assize-save"
            disabled={assize.isPending}
            onClick={() => void handleSave()}
          >
            {assize.isPending ? t('assize.saving') : t('assize.save')}
          </Button>
        </div>
      </div>
    </div>
  );
}

function Field({
  label,
  value,
  onChange,
  testId,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  testId: string;
}): JSX.Element {
  return (
    <label className="flex flex-col gap-1 text-sm">
      <span className="text-pw-text-muted">{label}</span>
      <Input
        type="number"
        min="0"
        data-testid={testId}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="h-9"
      />
    </label>
  );
}

export default AssizeModal;
